import React, { useState, useMemo } from 'react';
import { getCloudFunctionUrl, authenticatedFetch } from '../utils/cloudFunctions';
import './TurboAiSetup.css';

const TONE_LABELS = {
  1: 'Very Formal',
  2: 'Formal',
  3: 'Professional',
  4: 'Polished',
  5: 'Friendly Professional',
  6: 'Friendly',
  7: 'Approachable',
  8: 'Casual',
  9: 'Relaxed',
  10: 'Very Casual'
};

const TurboAiSetup = ({ isOpen, onClose, userId }) => {
  const [businessName, setBusinessName] = useState('');
  const [businessRole, setBusinessRole] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [businessGoals, setBusinessGoals] = useState('');
  const [targetNiches, setTargetNiches] = useState('');
  const [communicationTone, setCommunicationTone] = useState(5);
  const [additionalDetails, setAdditionalDetails] = useState('');

  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [generatedSettings, setGeneratedSettings] = useState({
    brandGuidelines: '',
    emailGuidelines: '',
    cleanupGuidelines: { keepSection: '', removeSection: '' },
    replyAgentGuidelines: '',
    safeGuardSettings: {
      emailDos: '',
      emailDonts: '',
      targetAudience: '',
      veryImportant: '',
      minimumRating: 6,
      enabled: true
    }
  });

  const compiledText = useMemo(() => {
    const parts = [];
    if (businessName || businessRole || companyName) {
      let intro = 'I am';
      if (businessName) intro += ` ${businessName}`;
      if (businessRole) intro += `, ${businessRole}`;
      if (companyName) intro += ` at ${companyName}`;
      parts.push(intro + '.');
    }
    if (businessGoals.trim()) {
      parts.push(`Our business goals: ${businessGoals.trim()}.`);
    }
    if (targetNiches.trim()) {
      parts.push(`Target niches and audiences we want to reach: ${targetNiches.trim()}.`);
    }
    parts.push(`Communication style: ${TONE_LABELS[communicationTone]}.`);
    if (additionalDetails.trim()) {
      parts.push(additionalDetails.trim());
    }
    return parts.join(' ');
  }, [businessName, businessRole, companyName, businessGoals, targetNiches, communicationTone, additionalDetails]);

  const isReadyToGenerate = compiledText.length >= 50;

  const generateAiSettings = async () => {
    if (!isReadyToGenerate || !userId) return;

    setIsGenerating(true);
    setCurrentStep(2);
    
    try {
      const response = await authenticatedFetch(getCloudFunctionUrl('turboAiAnalyze'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          extractedText: compiledText,
          userId: userId
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.settings) {
        const s = data.settings;
        const cleanedSettings = {
          brandGuidelines: typeof s.brandGuidelines === 'string' ? s.brandGuidelines : '',
          emailGuidelines: typeof s.emailGuidelines === 'string' ? s.emailGuidelines : '',
          cleanupGuidelines: {
            keepSection: typeof s.cleanupGuidelines?.keepSection === 'string' ? s.cleanupGuidelines.keepSection : '',
            removeSection: typeof s.cleanupGuidelines?.removeSection === 'string' ? s.cleanupGuidelines.removeSection : ''
          },
          replyAgentGuidelines: typeof s.replyAgentGuidelines === 'string' ? s.replyAgentGuidelines : '',
          safeGuardSettings: {
            emailDos: typeof s.safeGuardSettings?.emailDos === 'string' ? s.safeGuardSettings.emailDos : '',
            emailDonts: typeof s.safeGuardSettings?.emailDonts === 'string' ? s.safeGuardSettings.emailDonts : '',
            targetAudience: typeof s.safeGuardSettings?.targetAudience === 'string' ? s.safeGuardSettings.targetAudience : '',
            veryImportant: typeof s.safeGuardSettings?.veryImportant === 'string' ? s.safeGuardSettings.veryImportant : '',
            minimumRating: typeof s.safeGuardSettings?.minimumRating === 'number' ? s.safeGuardSettings.minimumRating : 6,
            enabled: typeof s.safeGuardSettings?.enabled === 'boolean' ? s.safeGuardSettings.enabled : true
          }
        };
        
        const textFields = ['brandGuidelines', 'emailGuidelines', 'replyAgentGuidelines'];
        const missingText = textFields.filter(f => !cleanedSettings[f]?.trim());
        if (missingText.length > 0) throw new Error(`Missing fields: ${missingText.join(', ')}`);
        
        if (!cleanedSettings.cleanupGuidelines.keepSection?.trim()) {
          throw new Error('Missing cleanup keepSection');
        }

        const sgFields = ['emailDos', 'emailDonts', 'targetAudience', 'veryImportant'];
        const missingSg = sgFields.filter(f => !cleanedSettings.safeGuardSettings[f]?.trim());
        if (missingSg.length > 0) throw new Error(`Missing SafeGuard fields: ${missingSg.join(', ')}`);
        
        setGeneratedSettings(cleanedSettings);
        setCurrentStep(3);
      } else {
        throw new Error(data.error || 'Failed to generate AI settings');
      }
    } catch (error) {
      console.error('Error generating AI settings:', error);
      alert('Error generating AI settings: ' + error.message);
      setCurrentStep(1);
    } finally {
      setIsGenerating(false);
    }
  };

  const saveSettings = async () => {
    if (!generatedSettings || !userId) return;

    const textFields = ['brandGuidelines', 'emailGuidelines', 'replyAgentGuidelines'];
    const missingText = textFields.filter(f => !generatedSettings[f]?.trim());
    if (missingText.length > 0) {
      alert(`Please fill in: ${missingText.join(', ')}`);
      return;
    }

    const sgFields = ['emailDos', 'emailDonts', 'targetAudience', 'veryImportant'];
    const missingSg = sgFields.filter(f => !generatedSettings.safeGuardSettings[f]?.trim());
    if (missingSg.length > 0) {
      alert(`Please fill SafeGuard: ${missingSg.join(', ')}`);
      return;
    }

    try {
      const response = await authenticatedFetch(getCloudFunctionUrl('updateAiSettings'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, settings: generatedSettings }),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const data = await response.json();
      if (data.success) {
        alert('AI and SafeGuard settings saved successfully!');
        handleClose();
      } else {
        throw new Error(data.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings: ' + error.message);
    }
  };

  const handleClose = () => {
    setBusinessName('');
    setBusinessRole('');
    setCompanyName('');
    setBusinessGoals('');
    setTargetNiches('');
    setCommunicationTone(5);
    setAdditionalDetails('');
    setCurrentStep(1);
    setGeneratedSettings({
      brandGuidelines: '',
      emailGuidelines: '',
      cleanupGuidelines: { keepSection: '', removeSection: '' },
      replyAgentGuidelines: '',
      safeGuardSettings: {
        emailDos: '', emailDonts: '', targetAudience: '', veryImportant: '',
        minimumRating: 6, enabled: true
      }
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="turboAiSetup-overlay">
      <div className="turboAiSetup-modal">
        <div className="turboAiSetup-header">
          <h2>
            <div className="turboAiSetup-header-icon">
              <img src="/img/star.svg" alt="AI Technology" />
            </div>
            Smart Settings AI Setup
          </h2>
          <button className="turboAiSetup-close" onClick={handleClose}>
            <img src="/img/tag_close.svg" alt="Close" />
          </button>
        </div>

        <div className="turboAiSetup-content">
          {/* Step 1: Structured Input */}
          {currentStep === 1 && (
            <div className="turboAiSetup-step">
              <div className="turboAiSetup-step-intro">
                <h3>Describe Your Business & Brand</h3>
                <p>Fill in the details below to generate personalized AI settings. The more you provide, the better the results.</p>
              </div>

              {/* Quick Info Fields */}
              <div className="turboAiSetup-section">
                <div className="turboAiSetup-section-label">
                  <img src="/img/user-one.svg" alt="" className="turboAiSetup-section-icon" />
                  About You
                </div>
                <div className="turboAiSetup-fields-row">
                  <div className="turboAiSetup-field">
                    <label>Your Name</label>
                    <input
                      type="text"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="e.g. Max Müller"
                    />
                  </div>
                  <div className="turboAiSetup-field">
                    <label>Your Role</label>
                    <input
                      type="text"
                      value={businessRole}
                      onChange={(e) => setBusinessRole(e.target.value)}
                      placeholder="e.g. CEO, Head of Sales"
                    />
                  </div>
                  <div className="turboAiSetup-field">
                    <label>Company</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="e.g. Acme Corp"
                    />
                  </div>
                </div>
              </div>

              {/* Business Goals */}
              <div className="turboAiSetup-section">
                <div className="turboAiSetup-section-label">
                  <img src="/img/bag.svg" alt="" className="turboAiSetup-section-icon" />
                  Describe Your Business Goals
                </div>
                <textarea
                  className="turboTextInput"
                  value={businessGoals}
                  onChange={(e) => setBusinessGoals(e.target.value)}
                  placeholder="e.g. We help mid-sized companies grow their online presence through tailored marketing strategies. Our goal is to acquire new B2B clients in the DACH region..."
                  maxLength={3000}
                  rows={3}
                />
              </div>

              {/* Target Niches */}
              <div className="turboAiSetup-section">
                <div className="turboAiSetup-section-label">
                  <img src="/img/target.svg" alt="" className="turboAiSetup-section-icon" />
                  Target Niches
                  <span className="turboAiSetup-section-hint">Separate with commas</span>
                </div>
                <input
                  type="text"
                  className="turboAiSetup-niche-input"
                  value={targetNiches}
                  onChange={(e) => setTargetNiches(e.target.value)}
                  placeholder="e.g. Hotels, Restaurants, Interior Design, Architecture, Real Estate Agencies"
                />
              </div>

              {/* Communication Tone Slider */}
              <div className="turboAiSetup-section">
                <div className="turboAiSetup-section-label">
                  <img src="/img/chat.svg" alt="" className="turboAiSetup-section-icon" />
                  Communication Tone
                </div>
                <div className="turboAiSetup-tone-container">
                  <div className="turboAiSetup-tone-slider-wrapper">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={communicationTone}
                      onChange={(e) => setCommunicationTone(parseInt(e.target.value))}
                      className="turboAiSetup-tone-slider"
                    />
                    <div className="turboAiSetup-tone-labels">
                      <span>Formal</span>
                      <span>Casual</span>
                    </div>
                  </div>
                  <div className="turboAiSetup-tone-badge">
                    {TONE_LABELS[communicationTone]}
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="turboAiSetup-section">
                <div className="turboAiSetup-section-label">
                  <img src="/img/edit.svg" alt="" className="turboAiSetup-section-icon" />
                  Additional Details
                  <span className="turboAiSetup-section-hint">Optional</span>
                </div>
                <textarea
                  className="turboTextInput"
                  value={additionalDetails}
                  onChange={(e) => setAdditionalDetails(e.target.value)}
                  placeholder="Anything else about your business? E.g. unique selling points, services, challenges your clients face, specific communication preferences..."
                  maxLength={5000}
                  rows={4}
                />
              </div>

              {/* Footer with character count and generate button */}
              <div className="turboAiSetup-step-footer">
                <div className="turboAiSetup-text-info">
                  <small>
                    Compiled: {compiledText.length} characters
                    {compiledText.length >= 50 ? 
                      <span className="turboAiSetup-info-good"> — Ready to generate</span> : 
                      <span className="turboAiSetup-info-warn"> — Fill in more details</span>
                    }
                  </small>
                </div>
                
                <div className="turboAiSetup-actions">
                  {isReadyToGenerate ? (
                    <button 
                      className="turboAiSetup-btn-primary"
                      onClick={generateAiSettings}
                      disabled={isGenerating}
                    >
                      <img src="/img/magic-wand.svg" alt="Generate" className="turboAiSetup-btn-icon" />
                      Generate AI Settings
                    </button>
                  ) : (
                    <p className="input-hint">
                      Fill in at least your name, role, and company to get started
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Generating */}
          {currentStep === 2 && (
            <div className="turboAiSetup-step">
              <div className="turboAiSetup-processing">
                <div className="turboAiSetup-spinner"></div>
                <h3>Creating Your AI Settings...</h3>
                <p>Analyzing your business and generating personalized settings...</p>
              </div>
            </div>
          )}

          {/* Step 3: Preview & Edit */}
          {currentStep === 3 && (
            <div className="turboAiSetup-step">
              <h3>Your Personalized AI Settings</h3>
              <p>Review and edit your AI settings below.</p>
              
              <div className="turboAiSetup-settings-preview">
                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/target.svg" alt="Target" />
                    </div>
                    Brand Guidelines
                  </h4>
                  <textarea 
                    value={generatedSettings.brandGuidelines}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      brandGuidelines: e.target.value
                    }))}
                    rows="6"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/mail_check.svg" alt="Email" />
                    </div>
                    Email Guidelines
                  </h4>
                  <textarea 
                    value={generatedSettings.emailGuidelines}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      emailGuidelines: e.target.value
                    }))}
                    rows="8"
                  />
                </div>

                {/* Cleanup Guidelines – neues Format mit KEEP / REMOVE */}
                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/binoculars.svg" alt="Filter" />
                    </div>
                    Contact Filtering
                  </h4>
                  <div className="turboAiSetup-cleanup-grid">
                    <div className="turboAiSetup-cleanup-section turboAiSetup-keep">
                      <div className="turboAiSetup-cleanup-badge keep-badge">KEEP</div>
                      <span className="turboAiSetup-cleanup-subtitle">Businesses to keep</span>
                      <textarea
                        value={generatedSettings.cleanupGuidelines.keepSection}
                        onChange={(e) => setGeneratedSettings(prev => ({
                          ...prev,
                          cleanupGuidelines: {
                            ...prev.cleanupGuidelines,
                            keepSection: e.target.value
                          }
                        }))}
                        rows="6"
                        placeholder="- Target audience characteristics..."
                      />
                    </div>
                    <div className="turboAiSetup-cleanup-section turboAiSetup-remove">
                      <div className="turboAiSetup-cleanup-badge remove-badge">REMOVE</div>
                      <span className="turboAiSetup-cleanup-subtitle">Businesses to exclude</span>
                      <textarea
                        value={generatedSettings.cleanupGuidelines.removeSection}
                        onChange={(e) => setGeneratedSettings(prev => ({
                          ...prev,
                          cleanupGuidelines: {
                            ...prev.cleanupGuidelines,
                            removeSection: e.target.value
                          }
                        }))}
                        rows="6"
                        placeholder="Doctors,Lawyers,Political parties..."
                      />
                    </div>
                  </div>
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/reply-message.svg" alt="Reply" />
                    </div>
                    Reply Guidelines
                  </h4>
                  <textarea 
                    value={generatedSettings.replyAgentGuidelines}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      replyAgentGuidelines: e.target.value
                    }))}
                    rows="6"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/setting-3.svg" alt="SafeGuard" />
                    </div>
                    SafeGuard: Email Dos
                  </h4>
                  <textarea 
                    value={generatedSettings.safeGuardSettings.emailDos}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      safeGuardSettings: { ...prev.safeGuardSettings, emailDos: e.target.value }
                    }))}
                    rows="6"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/setting-3.svg" alt="SafeGuard" />
                    </div>
                    SafeGuard: Email Don'ts
                  </h4>
                  <textarea 
                    value={generatedSettings.safeGuardSettings.emailDonts}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      safeGuardSettings: { ...prev.safeGuardSettings, emailDonts: e.target.value }
                    }))}
                    rows="6"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/setting-3.svg" alt="SafeGuard" />
                    </div>
                    SafeGuard: Target Audience
                  </h4>
                  <textarea 
                    value={generatedSettings.safeGuardSettings.targetAudience}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      safeGuardSettings: { ...prev.safeGuardSettings, targetAudience: e.target.value }
                    }))}
                    rows="4"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/setting-3.svg" alt="SafeGuard" />
                    </div>
                    SafeGuard: Very Important
                  </h4>
                  <textarea 
                    value={generatedSettings.safeGuardSettings.veryImportant}
                    onChange={(e) => setGeneratedSettings(prev => ({
                      ...prev,
                      safeGuardSettings: { ...prev.safeGuardSettings, veryImportant: e.target.value }
                    }))}
                    rows="4"
                  />
                </div>

                <div className="turboAiSetup-setting-group">
                  <h4>
                    <div className="turboAiSetup-setting-icon">
                      <img src="/img/setting-3.svg" alt="SafeGuard" />
                    </div>
                    SafeGuard: Settings
                  </h4>
                  <div className="turboAiSetup-safeGuard-controls">
                    <label className="turboAiSetup-checkbox-label">
                      <input
                        type="checkbox"
                        checked={generatedSettings.safeGuardSettings.enabled}
                        onChange={(e) => setGeneratedSettings(prev => ({
                          ...prev,
                          safeGuardSettings: { ...prev.safeGuardSettings, enabled: e.target.checked }
                        }))}
                      />
                      Enable SafeGuard
                    </label>
                    <label className="turboAiSetup-range-label">
                      Minimum Rating: {generatedSettings.safeGuardSettings.minimumRating}/10
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={generatedSettings.safeGuardSettings.minimumRating}
                        onChange={(e) => setGeneratedSettings(prev => ({
                          ...prev,
                          safeGuardSettings: { ...prev.safeGuardSettings, minimumRating: parseInt(e.target.value) }
                        }))}
                      />
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="turboAiSetup-actions">
                <button 
                  className="turboAiSetup-btn-secondary"
                  onClick={() => setCurrentStep(1)}
                >
                  <img src="/img/refresh-arrow.svg" alt="Back" className="turboAiSetup-btn-icon" />
                  Back to Input
                </button>
                <button 
                  className="turboAiSetup-btn-primary"
                  onClick={saveSettings}
                >
                  <img src="/img/check.svg" alt="Save" className="turboAiSetup-btn-icon" />
                  Save Settings
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TurboAiSetup;